/**
 * Lightweight reactive store for surfacing attachment validation errors in the UI.
 *
 * The attachment adapter runs outside React's tree (inside @assistant-ui/react internals),
 * so we use a tiny pub/sub store — identical to the pattern used by streamingMetadataStore.
 */

type Listener = () => void;

/** Sentinel when callers omit `surfaceId`; never matches a real `CuadraErrorSurfaceContext` id. */
export const UNESCOPED_ATTACHMENT_ERROR_SURFACE_ID =
  "__cuadra_unscoped_attachment_error__";

export interface AttachmentError {
  message: string;
  code: string;
  timestamp: number;
  /** Only the `SimpleThread` whose `CuadraErrorSurfaceContext` matches this id may show the toast */
  surfaceId: string;
}

class AttachmentErrorStore {
  private current: AttachmentError | null = null;
  private listeners = new Set<Listener>();

  /** Push a new error (triggers subscribers). */
  push(message: string, code: string, surfaceId?: string) {
    const resolved =
      surfaceId !== undefined &&
      surfaceId !== null &&
      String(surfaceId).trim() !== ""
        ? String(surfaceId).trim()
        : UNESCOPED_ATTACHMENT_ERROR_SURFACE_ID;
    this.current = {
      message,
      code,
      timestamp: Date.now(),
      surfaceId: resolved,
    };
    this.notify();
  }

  /** Clear the current error (triggers subscribers). */
  clear() {
    if (this.current) {
      this.current = null;
      this.notify();
    }
  }

  /** Clear only if the current error belongs to this surface (no-op otherwise). */
  clearIfSurface(surfaceId: string) {
    if (this.current && this.current.surfaceId === surfaceId) {
      this.current = null;
      this.notify();
    }
  }

  /** Get the current error (snapshot for useSyncExternalStore). */
  getSnapshot = (): AttachmentError | null => this.current;

  /** Subscribe (for useSyncExternalStore). Returns unsubscribe function. */
  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  private notify() {
    this.listeners.forEach((l) => l());
  }
}

export const attachmentErrorStore = new AttachmentErrorStore();
